import React, { createContext, useCallback, useContext, useRef, useState } from "react"

export type IngredientCategory =
  | "flour"
  | "water"
  | "salt"
  | "yeast"
  | "starter"
  | "sugar"
  | "butter"
  | "custom"

export interface Ingredient {
  id: string
  name: string
  weight: number
  percentage: number
  category: IngredientCategory
  locked?: boolean
}

export type StageType = "preferment" | "autolyse" | "soaker" | "custom"

export interface StageAllocation {
  ingredientId: string
  percentage: number
}

export interface DoughStage {
  id: string
  name: string
  type: StageType
  restTime?: string
  allocations: StageAllocation[]
}

export interface StageBreakdown {
  weight: number
  flourWeight: number
  waterWeight: number
  hydration: number
}

export interface FinalDoughItem {
  id: string
  name: string
  category: IngredientCategory
  allocatedWeight: number
  remainingWeight: number
}

export interface StageTimingGuidance {
  tempRange: string
  durationRange: string
  tip: string
}

interface CalculatorState {
  ingredients: Ingredient[]
  targetHydration: number
  starterRatio: number
  yieldOriginal: number
  yieldTarget: number
  stages: DoughStage[]
  formulaName: string
  bulkFermentationTime: string
  finalProofTime: string
}

interface CalculatorContextType extends CalculatorState {
  setIngredients: (ingredients: Ingredient[]) => void
  updateIngredientWeight: (id: string, weight: number) => void
  updateIngredientPercentage: (id: string, percentage: number) => void
  updateIngredientName: (id: string, name: string) => void
  addIngredient: (category: IngredientCategory, name?: string) => void
  removeIngredient: (id: string) => void
  setTargetHydration: (value: number) => void
  setStarterRatio: (value: number) => void
  setYieldOriginal: (value: number) => void
  setYieldTarget: (value: number) => void
  scaleByYield: () => void
  applyPanConversion: (factor: number) => void
  applySourdoughHydration: () => void
  totalDoughWeight: number
  totalTrueHydration: number
  flourWeight: number
  setFormulaName: (name: string) => void
  loadFormula: (
    ingredients: Ingredient[],
    hydration: number,
    stages?: DoughStage[],
    name?: string,
    bulkFermentationTime?: string,
    finalProofTime?: string
  ) => void
  addStage: (type: StageType) => void
  removeStage: (id: string) => void
  updateStageName: (id: string, name: string) => void
  updateStageRestTime: (id: string, restTime: string) => void
  updateStageAllocation: (stageId: string, ingredientId: string, percentage: number) => void
  getStageBreakdown: (stageId: string) => StageBreakdown
  finalDoughRemainder: FinalDoughItem[]
  finalDoughWeight: number
  setBulkFermentationTime: (value: string) => void
  setFinalProofTime: (value: string) => void
}

const DEFAULT_INGREDIENTS: Ingredient[] = [
  {
    id: "flour",
    name: "Total Flour",
    weight: 1000,
    percentage: 100,
    category: "flour",
    locked: true,
  },
  {
    id: "water",
    name: "Water",
    weight: 650,
    percentage: 65,
    category: "water",
  },
  {
    id: "salt",
    name: "Salt",
    weight: 20,
    percentage: 2,
    category: "salt",
  },
]

const guard = (val: number): number => {
  if (!isFinite(val) || isNaN(val)) return 0
  return Math.max(0, val)
}

const CATEGORY_NAMES: Record<IngredientCategory, string> = {
  flour: "Flour",
  water: "Water",
  salt: "Salt",
  yeast: "Yeast",
  starter: "Sourdough Starter",
  sugar: "Sugar",
  butter: "Butter",
  custom: "Custom Ingredient",
}

const STAGE_DEFAULTS: Record<StageType, { name: string; restTime: string }> = {
  preferment: { name: "Preferment", restTime: "8-16 hours" },
  autolyse: { name: "Autolyse", restTime: "20-60 min" },
  soaker: { name: "Soaker", restTime: "8-12 hours" },
  custom: { name: "Custom Stage", restTime: "" },
}

// Scheduling/timing reference guidance surfaced in the UI and printable sheet.
// Educational defaults only -- bakers should always adjust for their own kitchen conditions.
export const STAGE_TIMING_GUIDANCE: Record<StageType, StageTimingGuidance> = {
  preferment: {
    tempRange: "75-78°F (24-26°C) room temp",
    durationRange: "8-16 hours, or 12-16 hrs cold retard",
    tip: "Poolish, biga, and sponge preferments should roughly double in volume and look domed and bubbly before use. Longer, cooler ferments build more flavor complexity.",
  },
  autolyse: {
    tempRange: "68-72°F (20-22°C) room temp",
    durationRange: "20-60 minutes",
    tip: "Rest flour and water only (no salt or leavening) to jumpstart gluten development. 20-30 min is standard; whole-grain or high-extraction flours benefit from up to 60 min.",
  },
  soaker: {
    tempRange: "Room temp or refrigerated, 68-72°F (20-22°C)",
    durationRange: "8-12 hours",
    tip: "Soakers hydrate grains, seeds, or inclusions so they do not pull water from the final dough. Very hard grains can use a hot soaker with boiling water to speed hydration.",
  },
  custom: {
    tempRange: "Set based on your process",
    durationRange: "Define your own timing",
    tip: "Use this stage for any custom pre-ferment, scald, or inclusion step not covered by the presets above.",
  },
}

const DEFAULT_BULK_FERMENTATION_TIME = "4-6 hours at 75-78°F (24-26°C), or 12-18 hours cold retard"
const DEFAULT_FINAL_PROOF_TIME = "45-90 min at room temp, or 8-14 hours cold retard"

export const CalculatorContext = createContext<CalculatorContextType | null>(null)

export function CalculatorProvider({ children }: { children: React.ReactNode }) {
  const [ingredients, setIngredients] = useState<Ingredient[]>(DEFAULT_INGREDIENTS)
  const [targetHydration, setTargetHydration] = useState(65)
  const [starterRatio, setStarterRatio] = useState(20)
  const [yieldOriginal, setYieldOriginal] = useState(1)
  const [yieldTarget, setYieldTarget] = useState(2)
  const [stages, setStages] = useState<DoughStage[]>([])
  const [formulaName, setFormulaName] = useState("")
  const [bulkFermentationTime, setBulkFermentationTime] = useState(DEFAULT_BULK_FERMENTATION_TIME)
  const [finalProofTime, setFinalProofTime] = useState(DEFAULT_FINAL_PROOF_TIME)

  const idCounter = useRef(100)

  const getFlourWeight = useCallback((ings: Ingredient[]) => {
    const flourRow = ings.find((i) => i.locked)
    return flourRow ? flourRow.weight : 0
  }, [])

  const updateIngredientWeight = useCallback(
    (id: string, weight: number) => {
      setIngredients((prev) => {
        const flourWeight = getFlourWeight(prev)
        return prev.map((ing) => {
          if (ing.id !== id) return ing
          const newWeight = guard(weight)
          if (ing.locked) {
            // Flour changed: recompute all other percentages
            return { ...ing, weight: newWeight }
          }
          const pct = flourWeight > 0 ? guard((newWeight / flourWeight) * 100) : 0
          return { ...ing, weight: newWeight, percentage: pct }
        })
      })
    },
    [getFlourWeight]
  )

  // When flour changes, recompute weights of all non-locked items based on their percentages
  const updateFlourWeight = useCallback((weight: number) => {
    setIngredients((prev) => {
      const newFlour = guard(weight)
      return prev.map((ing) => {
        if (ing.locked) return { ...ing, weight: newFlour }
        const newWeight = guard((ing.percentage / 100) * newFlour)
        return { ...ing, weight: newWeight }
      })
    })
  }, [])

  const handleUpdateWeight = useCallback(
    (id: string, weight: number) => {
      setIngredients((prev) => {
        const isFlour = prev.find((i) => i.id === id)?.locked
        if (isFlour) {
          const newFlour = guard(weight)
          return prev.map((ing) => {
            if (ing.locked) return { ...ing, weight: newFlour }
            const newWeight = guard((ing.percentage / 100) * newFlour)
            return { ...ing, weight: newWeight }
          })
        }
        const flourWeight = getFlourWeight(prev)
        return prev.map((ing) => {
          if (ing.id !== id) return ing
          const newWeight = guard(weight)
          const pct = flourWeight > 0 ? guard((newWeight / flourWeight) * 100) : 0
          return { ...ing, weight: newWeight, percentage: pct }
        })
      })
    },
    [getFlourWeight]
  )

  const updateIngredientPercentage = useCallback(
    (id: string, percentage: number) => {
      setIngredients((prev) => {
        const flourWeight = getFlourWeight(prev)
        return prev.map((ing) => {
          if (ing.id !== id || ing.locked) return ing
          const newPct = guard(percentage)
          const newWeight = flourWeight > 0 ? guard((newPct / 100) * flourWeight) : 0
          return { ...ing, percentage: newPct, weight: newWeight }
        })
      })
    },
    [getFlourWeight]
  )

  const updateIngredientName = useCallback((id: string, name: string) => {
    setIngredients((prev) =>
      prev.map((ing) => (ing.id === id ? { ...ing, name } : ing))
    )
  }, [])

  const addIngredient = useCallback(
    (category: IngredientCategory, customName?: string) => {
      setIngredients((prev) => {
        const flourWeight = getFlourWeight(prev)
        idCounter.current += 1
        const id = `ing_${idCounter.current}`
        const name = customName || CATEGORY_NAMES[category]
        const pct = category === "water" ? 65 : category === "salt" ? 2 : 0
        const weight = flourWeight > 0 ? guard((pct / 100) * flourWeight) : 0
        return [
          ...prev,
          { id, name, weight, percentage: pct, category },
        ]
      })
    },
    [getFlourWeight]
  )

  const removeIngredient = useCallback((id: string) => {
    setIngredients((prev) => prev.filter((i) => i.id !== id || i.locked))
    // Keep stage allocations consistent if an allocated ingredient is removed
    setStages((prev) =>
      prev.map((stage) => ({
        ...stage,
        allocations: stage.allocations.filter((a) => a.ingredientId !== id),
      }))
    )
  }, [])

  const scaleByYield = useCallback(() => {
    if (yieldOriginal <= 0) return
    const factor = guard(yieldTarget / yieldOriginal)
    setIngredients((prev) =>
      prev.map((ing) => ({
        ...ing,
        weight: guard(ing.weight * factor),
      }))
    )
  }, [yieldOriginal, yieldTarget])

  const applyPanConversion = useCallback((factor: number) => {
    const f = guard(factor)
    if (f === 0) return
    setIngredients((prev) =>
      prev.map((ing) => {
        const newWeight = guard(ing.weight * f)
        return { ...ing, weight: newWeight }
      })
    )
    // Update percentages after scaling
    setIngredients((prev) => {
      const flourWeight = getFlourWeight(prev)
      return prev.map((ing) => {
        if (ing.locked) return ing
        const pct = flourWeight > 0 ? guard((ing.weight / flourWeight) * 100) : 0
        return { ...ing, percentage: pct }
      })
    })
  }, [getFlourWeight])

  const applySourdoughHydration = useCallback(() => {
    setIngredients((prev) => {
      const flourRow = prev.find((i) => i.locked)
      if (!flourRow || flourRow.weight <= 0) return prev

      const totalFlour = flourRow.weight
      const starterWeight = guard((starterRatio / 100) * totalFlour)
      const starterFlour = starterWeight / 2
      const starterWater = starterWeight / 2

      // targetHydration = (mainWater + starterWater) / (mainFlour + starterFlour)
      // mainFlour = totalFlour - starterFlour
      const mainFlour = totalFlour - starterFlour
      const totalWaterNeeded = guard((targetHydration / 100) * (mainFlour + starterFlour))
      const mainWater = guard(totalWaterNeeded - starterWater)

      // Update or create starter row
      const hasStarter = prev.some((i) => i.category === "starter")
      const mainWaterPct = mainFlour > 0 ? guard((mainWater / totalFlour) * 100) : 0

      let updated = prev.map((ing) => {
        if (ing.locked) return ing
        if (ing.category === "water") {
          return { ...ing, weight: mainWater, percentage: mainWaterPct }
        }
        if (ing.category === "starter") {
          const pct = guard((starterWeight / totalFlour) * 100)
          return { ...ing, weight: starterWeight, percentage: pct }
        }
        return ing
      })

      if (!hasStarter && starterWeight > 0) {
        idCounter.current += 1
        const starterPct = guard((starterWeight / totalFlour) * 100)
        updated = [
          ...updated,
          {
            id: `ing_${idCounter.current}`,
            name: "Sourdough Starter",
            weight: starterWeight,
            percentage: starterPct,
            category: "starter",
          },
        ]
      }

      return updated
    })
  }, [targetHydration, starterRatio])

  // --- Dough Stages (Preferments, Autolyse, Soakers) ---
  // Stages are a non-destructive allocation overlay: they carve a percentage of
  // each ingredient's existing weight into a planning stage without altering the
  // master spreadsheet weights.

  const addStage = useCallback(
    (type: StageType) => {
      idCounter.current += 1
      const id = `stage_${idCounter.current}`
      const defaults = STAGE_DEFAULTS[type]
      const allocations: StageAllocation[] = ingredients
        .map((ing) => {
          let pct = 0
          if (type === "preferment") {
            if (ing.category === "flour") pct = 20
            else if (ing.category === "water") pct = 20
            else if (ing.category === "yeast" || ing.category === "starter") pct = 100
          } else if (type === "autolyse") {
            if (ing.category === "flour" || ing.category === "water") pct = 100
          }
          return { ingredientId: ing.id, percentage: pct }
        })
        .filter((a) => a.percentage > 0)

      setStages((prev) => [
        ...prev,
        { id, name: defaults.name, type, restTime: defaults.restTime, allocations },
      ])
    },
    [ingredients]
  )

  const removeStage = useCallback((id: string) => {
    setStages((prev) => prev.filter((s) => s.id !== id))
  }, [])

  const updateStageName = useCallback((id: string, name: string) => {
    setStages((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)))
  }, [])

  const updateStageRestTime = useCallback((id: string, restTime: string) => {
    setStages((prev) => prev.map((s) => (s.id === id ? { ...s, restTime } : s)))
  }, [])

  const updateStageAllocation = useCallback(
    (stageId: string, ingredientId: string, percentage: number) => {
      setStages((prev) => {
        const otherStagesTotal = prev.reduce((sum, s) => {
          if (s.id === stageId) return sum
          const existing = s.allocations.find((alloc) => alloc.ingredientId === ingredientId)
          return sum + (existing ? existing.percentage : 0)
        }, 0)
        const maxAllowed = Math.max(0, 100 - otherStagesTotal)
        const clamped = Math.min(Math.max(0, guard(percentage)), maxAllowed)

        return prev.map((stage) => {
          if (stage.id !== stageId) return stage
          const existingIdx = stage.allocations.findIndex((a) => a.ingredientId === ingredientId)
          let allocations: StageAllocation[]
          if (existingIdx >= 0) {
            allocations = stage.allocations.map((a, i) =>
              i === existingIdx ? { ...a, percentage: clamped } : a
            )
          } else {
            allocations = [...stage.allocations, { ingredientId, percentage: clamped }]
          }
          return { ...stage, allocations }
        })
      })
    },
    []
  )

  const getStageBreakdown = useCallback(
    (stageId: string): StageBreakdown => {
      const stage = stages.find((s) => s.id === stageId)
      if (!stage) return { weight: 0, flourWeight: 0, waterWeight: 0, hydration: 0 }

      let weight = 0
      let flourWeight = 0
      let waterWeight = 0

      stage.allocations.forEach((alloc) => {
        const ing = ingredients.find((i) => i.id === alloc.ingredientId)
        if (!ing) return
        const w = guard((ing.weight * alloc.percentage) / 100)
        weight += w
        if (ing.category === "flour") flourWeight += w
        else if (ing.category === "water") waterWeight += w
        else if (ing.category === "starter") {
          // Starter assumed at 100% hydration: half flour, half water
          flourWeight += w / 2
          waterWeight += w / 2
        }
      })

      const hydration = flourWeight > 0 ? guard((waterWeight / flourWeight) * 100) : 0
      return {
        weight: guard(weight),
        flourWeight: guard(flourWeight),
        waterWeight: guard(waterWeight),
        hydration,
      }
    },
    [stages, ingredients]
  )

  // Derived totals
  const flourWeight = ingredients.find((i) => i.locked)?.weight ?? 0
  const totalDoughWeight = ingredients.reduce((sum, i) => sum + guard(i.weight), 0)

  const totalTrueHydration = (() => {
    let totalWater = 0
    let totalFlourForHydration = flourWeight

    ingredients.forEach((ing) => {
      if (ing.category === "water") totalWater += guard(ing.weight)
      if (ing.category === "starter") {
        totalWater += guard(ing.weight) / 2
        totalFlourForHydration += guard(ing.weight) / 2
      }
    })

    if (totalFlourForHydration <= 0) return 0
    return guard((totalWater / totalFlourForHydration) * 100)
  })()

  // What remains to be added at the final mix once stage allocations are subtracted
  const finalDoughRemainder: FinalDoughItem[] = ingredients.map((ing) => {
    const allocated = stages.reduce((sum, stage) => {
      const alloc = stage.allocations.find((a) => a.ingredientId === ing.id)
      return sum + (alloc ? guard((ing.weight * alloc.percentage) / 100) : 0)
    }, 0)
    const remaining = guard(ing.weight - allocated)
    return {
      id: ing.id,
      name: ing.name,
      category: ing.category,
      allocatedWeight: guard(allocated),
      remainingWeight: remaining,
    }
  })

  const finalDoughWeight = finalDoughRemainder.reduce((sum, r) => sum + r.remainingWeight, 0)

  const loadFormula = useCallback(
    (
      loadedIngredients: Ingredient[],
      hydration: number,
      loadedStages?: DoughStage[],
      name?: string,
      loadedBulkFermentationTime?: string,
      loadedFinalProofTime?: string
    ) => {
      setIngredients(loadedIngredients)
      setTargetHydration(hydration)
      setStages(loadedStages || [])
      setFormulaName(name || "")
      setBulkFermentationTime(loadedBulkFermentationTime || DEFAULT_BULK_FERMENTATION_TIME)
      setFinalProofTime(loadedFinalProofTime || DEFAULT_FINAL_PROOF_TIME)
    },
    []
  )

  return (
    <CalculatorContext.Provider
      value={{
        ingredients,
        targetHydration,
        starterRatio,
        yieldOriginal,
        yieldTarget,
        stages,
        formulaName,
        bulkFermentationTime,
        finalProofTime,
        setFormulaName,
        setIngredients,
        updateIngredientWeight: handleUpdateWeight,
        updateIngredientPercentage,
        updateIngredientName,
        addIngredient,
        removeIngredient,
        setTargetHydration,
        setStarterRatio,
        setYieldOriginal,
        setYieldTarget,
        scaleByYield,
        applyPanConversion,
        applySourdoughHydration,
        totalDoughWeight,
        totalTrueHydration,
        flourWeight,
        loadFormula,
        addStage,
        removeStage,
        updateStageName,
        updateStageRestTime,
        updateStageAllocation,
        getStageBreakdown,
        finalDoughRemainder,
        finalDoughWeight,
        setBulkFermentationTime,
        setFinalProofTime,
      }}
    >
      {children}
    </CalculatorContext.Provider>
  )
}

export function useCalculator() {
  const ctx = useContext(CalculatorContext)
  if (!ctx) throw new Error("useCalculator must be used inside CalculatorProvider")
  return ctx
}
